import { CurrencyFormatted, Typography, Button } from '@ui/components';
import { DashboardAccountResponse } from '@interfaces/models';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@shared/hooks';
import { cn } from '@ui/utils';

import { DAYS_LEFT_WARNING_THRESHOLD, BALANCE_CRITICAL_THRESHOLD } from './consts';
import { maskIban } from './utils';

interface DashboardWalletAccountsProps {
  accounts: DashboardAccountResponse[];
  openAccountsDetailsDialog: () => void;
}

const ACCOUNTS_COUNT_TO_SHOW_BUTTON = 4;
const ACCOUNT_TO_SHOW_IF_BUTTON_IS_VISIBLE = 2;

export const DashboardWalletAccounts = (props: DashboardWalletAccountsProps) => {
  const { accounts, openAccountsDetailsDialog } = props;
  const { currency } = useCurrentUser();
  const { t } = useTranslation();

  const isShowMoreButtonVisible = accounts.length >= ACCOUNTS_COUNT_TO_SHOW_BUTTON;

  const accountsToShow = isShowMoreButtonVisible
    ? accounts.slice(0, ACCOUNT_TO_SHOW_IF_BUTTON_IS_VISIBLE)
    : accounts;

  return (
    <div className="grid grid-cols-3 gap-2">
      {accountsToShow.map((account) => (
        <div className="border border-border-default rounded-md p-2" key={account.iban}>
          <Typography className="text-weak text-ellipsis mb-1" variant="labelSmallBook">
            {maskIban(account.iban)}
          </Typography>
          <Typography className="text-weak mb-1" variant="labelXSmallBold">
            {t('DASHBOARD.WALLET.ACCOUNT_BALANCE')}
          </Typography>
          <Typography
            className={cn('text-weak', {
              'text-warning': (account.daysLeft ?? Infinity) <= DAYS_LEFT_WARNING_THRESHOLD,
              'text-critical': account.balance === BALANCE_CRITICAL_THRESHOLD,
            })}
            variant="labelXSmallBold"
          >
            <CurrencyFormatted value={account.balance!} currency={currency} />
          </Typography>
        </div>
      ))}
      {isShowMoreButtonVisible && (
        <div className="border border-border-default rounded-md p-2 flex justify-center items-center text-wrap">
          <Button onClick={openAccountsDetailsDialog} variant="link">
            {t('DASHBOARD.WALLET.SEE_ALL_ACCOUNTS')}
          </Button>
        </div>
      )}
    </div>
  );
};
